export type PlaidErrorCode =
  // The user did not provide sufficient authorization in order to link their account via an OAuth login flow.
  | 'insufficient_credentials'
  // The financial institution indicated that the user's password or MFA information has changed. They will need to reauthenticate via Link's update mode.
  | 'item_login_required'
  // This error can happen when the public_token you provided is invalid, from a different API environment, or expired.
  | 'invalid_public_token'
  // Plaid is unable to support this user's accounts due to restrictions in place at the financial institution.
  | 'item_not_supported'
  // The financial institution indicated that the credentials provided were invalid.
  | 'invalid_credentials'
  // Plaid was unable to process the request, either due to an internal system issue or an unsupported response from a financial institution.
  | 'internal_server_error'
  // The financial institution is failing to respond to some of our requests. Your user may be successful if they retry another time.
  | 'institution_not_responding'

  /* Exit statuses */

  // User prompted to answer security question(s)
  | 'requires_questions'
  // User prompted to answer multiple choice question(s)
  | 'requires_selections'
  // User prompted to provide a one-time passcode
  | 'requires_code'
  // User prompted to select a device on which to receive a one-time passcode
  | 'choose_device'
  // User prompted to provide credentials for the selected financial institution or has not yet selected a financial institution
  | 'requires_credentials'
  // User exited the Link flow after unsuccessfully (no results returned) searching for a financial institution
  | 'institution_not_found'
  // User exited the flow without being redirected to their bank's website/mobile app, or their bank failed to redirect them back to Plaid.
  | 'requires_oauth'
  // Invalid link token (tokens are invalid after 30m or too many failed logins)
  | 'invalid_link_token'

export type ErrorCode =
  // User has attempted to connect to their bank too many times. This is a limit set by us per quote.
  // The user can fix this by adding a new quote.
  | 'too_many_connections'
  // User was stuck in the payment loading screen for too long while we wait for Plaid to provide us with historical transactions.
  // This cna be because there was an error but the bank didn't inform Plaid.
  // This might be fixed if the user uses Plaid's update mode to re-authenticate with the bank.
  | 'transactions_progress_timeout'
  // Plaid sent us a webhook informing us there was an error while the user was at the payment loading screen.
  // This might be fixed if the user uses Plaid's update mode to re-authenticate with the bank.
  | 'transactions_progress_error'
  // The user got stuck at our loading page for too long after getting redirected back from Plaid/their bank.
  | 'plaid_post_redirect_event_timeout'
  // There was a problem when submitting open banking data to our own backend service.
  | 'open_banking_backend_error'
  // We detected that the user did not select a current account with the metadata available to the frontend.
  | 'wrong_account_metadata'
  // The backend determined that the account data submitted does not belong to a current account.
  | 'wrong_account_ob1'
  // There was a frontend error while attempting to submit account data to the backend.
  | 'submit_account_data_frontend_error'
  // The Plaid connection that was initially set up is more than 30 minutes old.
  // A new one must be created.
  | 'connection_expired'
  // We got a 403 from Quoting after getting redirected back from Plaid, which means that the user does not have permissions to access this quote.
  // This can happen if the bank redirects the user to the default browser, and this is not the same in which they started their session with.
  // It can also happen if they take too long to authenticate with their bank.
  | 'open_banking_session_expired'
  // Same as above, but not open banking related.
  | 'session_expired'
  // User got declined for fraud after attempting to pay.
  | 'payment_fraud_decline'
  // User got declined for an open banking reason after attempting to pay.
  | 'payment_open_banking_decline'
  // User was declined before reaching the payment stage.
  | 'pre_payment_decline'
  // This quote does not belong to the Open Banking segment, as we did not save an oauth nonce for it.
  | 'wrong_segment'
  // Our backend returned an unexpected transactions progress response
  | 'unexpected_transactions_progress_response'
  // Resume token was not set in either local storage or query fragment
  | 'resume_token_unavailable'
  // Unable to resume quote with resume token (we weren't able to set the required cookies)
  | 'unable_to_resume'
  // Unable to get resume token from quoting using current cookies
  | 'unable_to_fetch_resume_token'
  // Link token endpoint failed to return a valid response
  | 'unable_to_create_link_token'

// References below are used in the below notion:
// https://www.notion.so/getmarshmallow/Open-Banking-Error-Codes-e812d4289f954cc7a69b57bd093d1366?pvs=4
export type ErrorReference =
  | '0000'
  | '4319'
  | '6811'
  | '6812'
  | '9210'
  | '5754'
  | '4746'
  | '4629'
  | '5628'
  | '4621'
  | '4738'
  | '4728'
  | '4799'
  | '4729'
  | '9387'
  | '0019'
  | '9819'
  | '0927'
  | '4929'
  | '4525'
  | '1274'
  | '8476'
  | '4991'
  | '2766'
  | '4762'
  | '4763'
  | '7472'
  | '4821'
  | '4727'
  | '4827'
  | '4627'
  | '5667'
  | '4311'
  | '4221'
  | '3721'

export const ERROR_CODE_TO_REFERENCE = new Map<ErrorCode, ErrorReference>([
  ['too_many_connections', '4319'],
  ['transactions_progress_timeout', '6811'],
  ['transactions_progress_error', '6812'],
  ['plaid_post_redirect_event_timeout', '5754'],
  ['open_banking_backend_error', '4746'],
  ['wrong_account_metadata', '4629'],
  ['wrong_account_ob1', '5628'],
  ['submit_account_data_frontend_error', '4621'],
  ['connection_expired', '4738'],
  ['open_banking_session_expired', '4762'],
  ['session_expired', '4763'],
  ['payment_fraud_decline', '7472'],
  ['payment_open_banking_decline', '4821'],
  ['pre_payment_decline', '4727'],
  ['wrong_segment', '4827'],
  ['unexpected_transactions_progress_response', '5667'],
  ['resume_token_unavailable', '4311'],
  ['unable_to_resume', '4221'],
  ['unable_to_fetch_resume_token', '3721'],
])

export const PLAID_ERROR_CODE_TO_REFERENCE = new Map<PlaidErrorCode, ErrorReference>([
  ['item_login_required', '9210'],
  ['invalid_public_token', '4728'],
  ['invalid_link_token', '4799'],
  ['item_not_supported', '4729'],
  ['insufficient_credentials', '2766'],
  ['invalid_credentials', '9387'],
  ['internal_server_error', '0019'],
  ['institution_not_responding', '9819'],
  ['requires_questions', '0927'],
  ['requires_selections', '4929'],
  ['requires_code', '4525'],
  ['choose_device', '1274'],
  ['requires_credentials', '8476'],
  ['institution_not_found', '4991'],
  ['requires_oauth', '4627'],
])

export const isErrorCode = (value?: string): value is ErrorCode => {
  return !!Array.from(ERROR_CODE_TO_REFERENCE.keys()).find((key) => !!value && key === value)
}

export const isPlaidErrorCode = (value?: string): value is PlaidErrorCode => {
  return !!Array.from(PLAID_ERROR_CODE_TO_REFERENCE.keys()).find((key) => !!value && key === value)
}
